import { AnyAri } from '@atlassian/ari';
import { resolveConnectionContent } from '@databases/connections/content/contentLoad';
import { loadAsync } from '@databases/utils/redux/redux';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { DefaultRootState } from '../redux';

import {
  ConnectionContentWithColumns,
  selectContentOptional,
  selectContentStatus,
} from './selectors';
import connectionsSlice, { Content, ContentItems } from './slice';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

export const loadContent = ({
  ari,
  connectionId,
  columns,
  forceReload,
}: {
  ari: string;
  connectionId?: string | undefined;
  columns?: string[] | undefined;
  forceReload?: boolean;
}): Dispatcher<Promise<ConnectionContentWithColumns | undefined>> => {
  return async (dispatch, getState) => {
    const state = getState().connections;
    const content: Content | undefined = state.content[ari];
    if (!connectionId && content) {
      connectionId = state.content[ari]['connectionId'];
    }
    if (!connectionId) {
      return undefined;
    }

    const connection = state.connections[connectionId];
    if (!connection) {
      return undefined;
    }

    await loadAsync(
      () => selectContentStatus(getState().connections, ari),
      () => {
        dispatch(connectionsSlice.actions.loadContentPending({ ari }));
      },
      () => {
        return resolveConnectionContent(
          ari,
          columns ?? connection.requiredColumns,
          'confluence',
          AnyAri.parse(ari).cloudId,
          connectionId ?? ''
        );
      },
      // eslint-disable-next-line require-await
      async (responses) => {
        if (responses) {
          responses.forEach(({ connectionId, datasourceResponse }) => {
            datasourceResponse.data.items.forEach((item) => {
              dispatch(
                connectionsSlice.actions.addContentToList({
                  ari: item.id.data.toString(),
                  connectionId,
                  item: item as ContentItems,
                  columns: datasourceResponse.data.schema?.properties || [],
                })
              );
              dispatch(
                connectionsSlice.actions.loadContentFinished({
                  connectionId,
                  ari: item.id.data.toString(),
                  access: datasourceResponse.meta.access,
                })
              );
            });
          });
        }
      },
      (error) => {
        dispatch(connectionsSlice.actions.loadContentError({ ari, error }));
      },
      { forceReload }
    );

    return selectContentOptional(state, ari);
  };
};
